<script setup>
import { CalendarCheck, ChevronDown, LogOut, Menu, Settings, ShieldCheck, User, Wallet } from 'lucide-vue-next';
import { canSeeAnyDashboard, cn } from '~/lib/utils';

const config = useRuntimeConfig();
const route = useRoute();
const user = useUser();
const cosmo = useCosmo();

const isMobileMenuOpen = ref(false);
const isAboutOpen = ref(false);
const isScrolled = ref(false);
let scrollRafId = null;

const isMain = computed(() => config?.public?.isMain);
const isHome = computed(() => route.meta.layout === 'home');
const isImmersive = computed(() => isHome.value || route.meta.immersiveNav === true);
const isSignedIn = computed(() => Boolean(user.getUser?.id));
const canSeeMarketPlace = () => isMain.value && user.hasFeature('MARKET_PLACE');

// Text sits on the dark bar, except over the hero before scrolling
const onDark = computed(() => true);
const solidBar = computed(() => !isImmersive.value || isScrolled.value);

const navigationItems = computed(() => [
  { href: '/search', label: 'Reserve' },
  { href: '/parking-packs', label: 'Season Passes', disabled: !globalConfig().hasPacks && !isMain.value },
  { href: '/market', label: 'Specials', disabled: !canSeeMarketPlace() },
  { href: '/owner-signup', label: 'List Your Spots', disabled: !isMain.value },
].filter((item) => !item.disabled));

const aboutUsNavigationItems = computed(() => [
  { href: '/investors', label: 'Investors' },
  { href: '/FAQs', label: 'FAQs' },
  { href: '/news-happenings', label: 'News', disabled: !isMain.value },
  { href: '/contact-us', label: 'Contact Us' },
  { href: '/about', label: 'Company' },
].filter((item) => !item.disabled));

const userNavigationLinks = computed(() => [
  { href: '/dashboard', label: 'My Reservations', icon: CalendarCheck, disabled: !canSeeAnyDashboard(user.getUser?.role) },
  { href: '/admin', label: 'Admin Dashboard', icon: ShieldCheck, disabled: !user.getUser?.is_staff },
  { href: '/dashboard/wallet', label: 'My Wallet', icon: Wallet, disabled: user.getUser?.role !== 'PARKER' },
  { href: '/dashboard/settings', label: 'Settings', icon: Settings },
].filter((item) => !item.disabled));

const isActive = (href) => route.path.toLowerCase() === href.toLowerCase() || route.path.toLowerCase().startsWith(`${href.toLowerCase()}/`);
const aboutIsActive = computed(() => aboutUsNavigationItems.value.some((item) => isActive(item.href)));

const textTone = computed(() => (onDark.value ? 'text-white' : 'text-primary'));
const linkClass = (active) => cn(
  'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
  'hover:text-accent-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-orange',
  textTone.value,
  active && (onDark.value ? 'bg-white/10' : 'bg-primary/10')
);

// Dropdown menus are white, so set an explicit dark text color instead of inheriting the global active-link color
const menuLinkClass = (active) => cn(
  'flex w-full items-center gap-2 rounded-sm text-foreground',
  active && 'font-semibold'
);

const logout = () => {
  isMobileMenuOpen.value = false;
  useLogout();
};

watch(() => route.fullPath, () => {
  isMobileMenuOpen.value = false;
});

const desktopQuery = typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)') : null;
const closeMenuOnDesktop = (event) => {
  if (event.matches) isMobileMenuOpen.value = false;
};

const handleScroll = () => {
  if (scrollRafId) return;
  scrollRafId = requestAnimationFrame(() => {
    isScrolled.value = window.scrollY > 24;
    scrollRafId = null;
  });
};

const setupScrollListener = () => {
  if (isImmersive.value) {
    window.addEventListener('scroll', handleScroll, { passive: true });
    isScrolled.value = window.scrollY > 50;
  } else {
    window.removeEventListener('scroll', handleScroll);
    isScrolled.value = false;
  }
};

watch(isImmersive, setupScrollListener);
onMounted(() => {
  setupScrollListener();
  desktopQuery?.addEventListener('change', closeMenuOnDesktop);
});

onUnmounted(() => {
  desktopQuery?.removeEventListener('change', closeMenuOnDesktop);
  window.removeEventListener('scroll', handleScroll);
  if (scrollRafId) {
    cancelAnimationFrame(scrollRafId);
    scrollRafId = null;
  }
});
</script>

<template>
  <header
    class="fixed top-0 z-30 transition-all duration-500 ease-out"
    :class="solidBar
      ? 'bg-slate-950/95 shadow-lg shadow-slate-950/10 backdrop-blur-xl'
      : 'bg-transparent shadow-none backdrop-blur-none'"
    :style="{ width: cosmo.isOpen ? 'calc(100vw - 400px)' : '100vw' }"
  >
    <nav class="mx-auto flex h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8" aria-label="Main">
      <NuxtLink v-if="isMain" to="/" class="flex shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-orange" aria-label="Spotsurfer home">
        <NuxtImg class="h-10 w-10 sm:h-11 sm:w-11" src="/images/spotsurferLogo.png" alt="" />
        <span class="flex flex-col leading-none">
          <span class="text-xl font-semibold uppercase sm:text-2xl" :class="textTone">{{ config?.public?.siteName }}</span>
          <span class="mt-1 hidden text-[10px] uppercase tracking-[0.25em] sm:block" :class="textTone">Reserve. Park. Play.</span>
        </span>
      </NuxtLink>
      <NuxtLink v-else to="/" class="flex shrink-0 items-center">
        <NuxtImg
          class="max-h-16 w-auto object-cover py-2"
          style="min-width: 100px; max-width: 200px;"
          :src="globalConfig()?.logo?.url"
          :alt="`${globalConfig()?.logo?.name}`"
        />
        <span class="px-2 text-xl font-semibold uppercase tracking-wider" :class="textTone">- parking</span>
      </NuxtLink>

      <!-- Desktop -->
      <div class="hidden items-center gap-1 lg:flex">
        <NuxtLink
          v-for="item in navigationItems"
          :key="item.href"
          :to="item.href"
          :class="linkClass(isActive(item.href))"
          :aria-current="isActive(item.href) ? 'page' : undefined"
        >
          {{ item.label }}
        </NuxtLink>

        <DropdownMenu v-if="isMain" v-model:open="isAboutOpen">
          <DropdownMenuTrigger :class="linkClass(aboutIsActive)" aria-label="About us menu">
            About
            <ChevronDown class="ml-1 h-4 w-4 transition-transform" :class="{ 'rotate-180': isAboutOpen }" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-48">
            <DropdownMenuItem v-for="item in aboutUsNavigationItems" :key="item.href" as-child :class="cn('cursor-pointer', isActive(item.href) && 'bg-muted')">
              <NuxtLink
                :to="item.href"
                :class="menuLinkClass(isActive(item.href))"
                :aria-current="isActive(item.href) ? 'page' : undefined"
              >{{ item.label }}</NuxtLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div class="ml-3 flex items-center gap-2 border-l pl-4" :class="onDark ? 'border-white/20' : 'border-primary/30'">
          <template v-if="isSignedIn">
            <DropdownMenu>
              <DropdownMenuTrigger
                class="flex items-center gap-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-orange"
                aria-label="Account menu"
              >
                <NuxtImg v-if="user.getUser?.avatar" class="h-9 w-9 rounded-full border-2 object-cover" :class="onDark ? 'border-white/60' : 'border-primary'" :src="user.getUser.avatar" alt="" />
                <span v-else class="flex h-9 w-9 items-center justify-center rounded-full border-2" :class="[textTone, onDark ? 'border-white/60' : 'border-primary']">
                  <User class="h-5 w-5" />
                </span>
                <ChevronDown class="h-4 w-4" :class="textTone" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" class="w-60">
                <DropdownMenuLabel class="truncate font-normal">
                  <span class="block text-xs text-muted-foreground">Signed in as</span>
                  <span class="block truncate text-sm font-medium">{{ user.getUser?.email }}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem v-for="item in userNavigationLinks" :key="item.href" as-child :class="cn('cursor-pointer', isActive(item.href) && 'bg-muted')">
                  <NuxtLink
                    :to="item.href"
                    :class="menuLinkClass(isActive(item.href))"
                    :aria-current="isActive(item.href) ? 'page' : undefined"
                  ><component :is="item.icon" />{{ item.label }}</NuxtLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem class="cursor-pointer" @select="logout"><LogOut />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </template>
          <template v-else>
            <NuxtLink to="/login" :class="linkClass(isActive('/login'))">Log in</NuxtLink>
            <NuxtLink
              to="/signup"
              class="inline-flex items-center rounded-md bg-orange-400 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Sign up
            </NuxtLink>
          </template>
        </div>
      </div>

      <!-- Mobile trigger -->
      <button
        type="button"
        class="rounded-md p-1 transition-colors hover:text-accent-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-orange lg:hidden"
        :class="textTone"
        aria-label="Open menu"
        :aria-expanded="isMobileMenuOpen"
        @click="isMobileMenuOpen = true"
      >
        <Menu class="h-8 w-8" />
      </button>
    </nav>

    <!-- Mobile menu -->
    <Sheet v-model:open="isMobileMenuOpen">
      <SheetContent overlay-class="z-[999998]" class="z-[999999] flex w-[88vw] max-w-sm flex-col border-none bg-slate-950 p-0 text-white shadow-lg [&>button>svg]:text-white [&>button]:opacity-100">
        <SheetHeader class="sr-only">
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <SheetDescription class="sr-only">Main application menu</SheetDescription>

        <div class="flex h-16 shrink-0 items-center border-b border-white/10 px-4">
          <NuxtLink v-if="isMain" to="/" class="flex items-center gap-2">
            <NuxtImg class="h-9 w-9" src="/images/spotsurferLogo.png" alt="" />
            <span class="text-xl font-semibold uppercase text-white">{{ config?.public?.siteName }}</span>
          </NuxtLink>
          <NuxtLink v-else to="/">
            <NuxtImg class="max-h-12 w-auto object-contain" style="max-width: 160px;" :src="globalConfig()?.logo?.url" :alt="`${globalConfig()?.logo?.name}`" />
          </NuxtLink>
        </div>

        <div class="flex-1 overflow-y-auto px-2 py-3">
          <ul class="space-y-1">
            <li v-for="item in navigationItems" :key="item.href">
              <NuxtLink
                :to="item.href"
                class="flex items-center rounded-lg px-3 py-3 text-base font-medium transition-colors hover:bg-white/5 hover:text-accent-orange"
                :class="isActive(item.href) ? 'bg-white/10 text-accent-orange' : 'text-white'"
                :aria-current="isActive(item.href) ? 'page' : undefined"
              >
                {{ item.label }}
              </NuxtLink>
            </li>
          </ul>

          <template v-if="isSignedIn">
            <p class="mt-5 px-3 text-xs font-semibold uppercase tracking-widest text-white/80">Account</p>
            <ul class="mt-1 space-y-1">
              <li v-for="item in userNavigationLinks" :key="item.href">
                <NuxtLink
                  :to="item.href"
                  class="flex items-center gap-3 rounded-lg px-3 py-3 text-base transition-colors hover:bg-white/5 hover:text-accent-orange"
                  :class="isActive(item.href) ? 'bg-white/10 text-accent-orange' : 'text-white'"
                >
                  <component :is="item.icon" class="h-5 w-5" />{{ item.label }}
                </NuxtLink>
              </li>
            </ul>
          </template>

          <Collapsible v-if="isMain" class="mt-5">
            <CollapsibleTrigger class="group flex w-full items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-white transition-colors hover:bg-white/5">
              About us
              <ChevronDown class="h-5 w-5 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul class="space-y-1 pb-1 pl-3">
                <li v-for="item in aboutUsNavigationItems" :key="item.href">
                  <NuxtLink
                    :to="item.href"
                    class="block rounded-lg px-3 py-2.5 text-base transition-colors hover:bg-white/5 hover:text-accent-orange"
                    :class="isActive(item.href) ? 'text-accent-orange' : 'text-white'"
                  >
                    {{ item.label }}
                  </NuxtLink>
                </li>
              </ul>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <div class="shrink-0 border-t border-white/10 p-4">
          <button
            v-if="isSignedIn"
            type="button"
            class="flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 px-4 py-3 text-base font-medium text-white transition-colors hover:bg-white/10"
            @click="logout"
          >
            <LogOut class="h-5 w-5" />Log out
          </button>
          <div v-else class="grid grid-cols-2 gap-3">
            <NuxtLink to="/login" class="rounded-lg border border-white/20 px-4 py-3 text-center text-base font-medium text-white transition-colors hover:bg-white/10">Log in</NuxtLink>
            <NuxtLink to="/signup" class="rounded-lg bg-orange-400 px-4 py-3 text-center text-base font-semibold text-black transition-colors hover:bg-orange-500">Sign up</NuxtLink>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  </header>
</template>
